import { Button, DialogActions, DialogContent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSendDeviceCommand } from '~/api/devices';
import { RemoteControl } from '~/components/devices';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import type { DeviceBulkSelectionInput } from '~/generated/graphql';
import { DeviceCommandKind, RemoteButton } from '~/generated/graphql';

interface RemoteControlDialogProps {
  onClose: () => void;
  open: boolean;
  selected: DeviceBulkSelectionInput;
  /** Whether mute/unmute can reach the device. See `Device#supports_mute?`. */
  supportsMute: boolean;
  /** Whether the +/- keys can step the device's volume. See `Device#supports_volume?`. */
  supportsVolume: boolean;
}

// Navigation keys ride the remote_control input-event path; volume is not an
// input event on the player, it has its own relative commands, so the +/- keys
// are mapped onto those instead.
const VOLUME_COMMANDS: Partial<Record<RemoteButton, DeviceCommandKind>> = {
  [RemoteButton.VolumeDown]: DeviceCommandKind.VolumeDown,
  [RemoteButton.VolumeUp]: DeviceCommandKind.VolumeUp,
};

// The dialog stays open after each press so the user can drive a sequence of
// navigation inputs (e.g. DOWN DOWN OK), mirroring how a physical remote works.
export const RemoteControlDialog = ({
  onClose,
  open,
  selected,
  supportsMute,
  supportsVolume,
}: RemoteControlDialogProps) => {
  const { t } = useTranslation();
  const [sendCommand] = useSendDeviceCommand();

  const send = (command: DeviceCommandKind, payload: Record<string, unknown> = {}) =>
    void sendCommand({
      variables: {
        input: {
          command,
          payload,
          selected,
        },
      },
    });

  const press = (button: RemoteButton) => {
    const volumeCommand = VOLUME_COMMANDS[button];

    if (volumeCommand) {
      send(volumeCommand);
      return;
    }

    send(DeviceCommandKind.RemoteControl, { button });
  };

  // Mute isn't a remote-control input event either, and it has no dedicated command
  // kind — it rides the same arbitrary path the command popover uses.
  const setMuted = (muted: boolean) =>
    send(DeviceCommandKind.Arbitrary, { arbitrary: muted ? 'mute' : 'unmute' });

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
      <DialogTitle onClose={onClose}>{t('pages.devices.dialogs.remote_control.title')}</DialogTitle>

      <DialogContent>
        <RemoteControl
          onPress={press}
          onSetMuted={setMuted}
          supportsMute={supportsMute}
          supportsVolume={supportsVolume}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('common.actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
